/**
 * Left Drawer Store
 *
 * This store provides outer functionality and computed getters for the left drawer component.
 * The internal state cannot be modified directly from outside - state changes can only be
 * performed by calling the provided functions. This ensures controlled state management
 * and prevents unauthorized state mutations.
 *
 * Features:
 * - Computed properties for reactive state checking
 * - Controlled state modification through dedicated functions
 * - Empty drawer setup ready for customization
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";

export const useLeftDrawerStore = defineStore("leftDrawer", () => {
    /**
     * Available drawer modules
     * @readonly
     */
    const MODULES = {
        NONE: "",
        QUICK_ACTION: "quickAction",
        SEARCH: "search",
        WSC_ASSOCIATION_FILTER: "wscAssociationFilter",
        NEW_WORKSPACE: "newWorkspace",
        DIVISION_SPACES_FILTER: "divisionSpacesFilter",
        HISTORY: "history",
    };

    /**
     * Default drawer width
     * @readonly
     */
    const WIDTH = 450;

    /**
     * Width for full-height overlay drawers (Quick Actions, Search, Divisions filter, etc.)
     * @readonly
     */
    const FULL_OVERLAY_WIDTH = 401;

    /**
     * Internal drawer state - cannot be modified directly from outside
     * @private
     */
    const drawerState = ref({
        open: false,
        module: MODULES.NONE,
        width: WIDTH,
        full: false,
    });

    /** True while search panel locate overlay / navigation is in progress. */
    const searchLocating = ref(false);

    /**
     * Computed property to check if any drawer module is currently active
     * @returns {boolean} True if drawer is open and has an active module
     */
    const isActiveDrawer = computed(
        () =>
            drawerState.value.open && drawerState.value.module !== MODULES.NONE,
    );

    /**
     * Computed property to check if quick action module is currently active
     * @returns {boolean} True if quick action module is active
     */
    const isQuickActionActive = computed(() =>
        isModuleActive(MODULES.QUICK_ACTION),
    );

    /**
     * Computed property to check if search module is currently active
     * @returns {boolean} True if search module is active
     */
    const isSearchActive = computed(() => isModuleActive(MODULES.SEARCH));

    const isSearchLocating = computed(() => searchLocating.value);

    /**
     * Computed property to check if wsc association filter module is currently active
     * @returns {boolean} True if wsc association filter module is active
     */
    const isWSCAssociationFilterActive = computed(() =>
        isModuleActive(MODULES.WSC_ASSOCIATION_FILTER),
    );

    /**
     * Computed property to check if new workspace module is currently active
     * @returns {boolean} True if new workspace module is active
     */
    const isNewWorkspaceActive = computed(() =>
        isModuleActive(MODULES.NEW_WORKSPACE),
    );

    /**
     * Computed property to check if division spaces filter module is currently active
     * @returns {boolean} True if division spaces filter module is active
     */
    const isDivisionSpacesFilterActive = computed(() =>
        isModuleActive(MODULES.DIVISION_SPACES_FILTER),
    );

    const isHistoryActive = computed(() => isModuleActive(MODULES.HISTORY));

    /**
     * Computed property to get the current drawer width
     * @returns {number} Current drawer width
     */
    const currentWidth = computed(() => drawerState.value.width);

    /**
     * Computed property to check if drawer is in full screen mode
     * @returns {boolean} True if drawer is in full screen mode
     */
    const isFullDrawer = computed(() => drawerState.value.full);

    /**
     * Check if a specific module is currently active
     * @param {string} module - The module to check
     * @returns {boolean} True if the specified module is active and drawer is open
     */
    function isModuleActive(module) {
        return drawerState.value.module === module && drawerState.value.open;
    }

    /**
     * Open the drawer with a specific module
     * This is the primary way to activate the drawer with a module from outside
     * @param {string} module - The module to activate
     * @param {number} width - Optional width parameter (defaults to WIDTH constant)
     * @param {boolean} full - Optional full screen parameter (defaults to false)
     */
    function openDrawer(module, width = WIDTH, full = false) {
        drawerState.value.open = true;
        drawerState.value.module = module;
        drawerState.value.width = width;
        drawerState.value.full = full;
    }

    /**
     * Close the drawer and reset module to NONE
     * This is the only way to close the drawer from outside
     */
    function closeDrawer() {
        drawerState.value.open = false;
        drawerState.value.module = MODULES.NONE;
        drawerState.value.width = WIDTH;
        drawerState.value.full = false;
        searchLocating.value = false;
    }

    function setSearchLocating(locating) {
        searchLocating.value = Boolean(locating);
    }

    /**
     * Update the drawer width
     * This allows dynamic width changes while the drawer is open
     * @param {number} width - The new width value
     */
    function setWidth(width) {
        drawerState.value.width = width;
    }

    /**
     * Open the quick action module
     * This opens the drawer and activates the quick action module
     * @param {number} width - Optional width parameter (defaults to WIDTH constant)
     */
    function openQuickAction(width = FULL_OVERLAY_WIDTH) {
        openDrawer(MODULES.QUICK_ACTION, width, true);
    }

    /**
     * Open the search module
     * This opens the drawer and activates the search module
     * @param {number} width - Optional width parameter (defaults to WIDTH constant)
     */
    function openSearch(width = FULL_OVERLAY_WIDTH) {
        openDrawer(MODULES.SEARCH, width, true);
    }

    /**
     * Open the wsc association filter module
     * This opens the drawer and activates the wsc association filter module
     * @param {number} width - Optional width parameter (defaults to WIDTH constant)
     */
    function openWSCAssociationFilter(width = FULL_OVERLAY_WIDTH) {
        openDrawer(MODULES.WSC_ASSOCIATION_FILTER, width, false);
    }

    /**
     * Open the new workspace module (full-height teleported overlay; stacks above pinned activity).
     * @param {number} width - Optional width parameter (defaults to FULL_OVERLAY_WIDTH)
     */
    function openNewWorkspace(width = FULL_OVERLAY_WIDTH) {
        openDrawer(MODULES.NEW_WORKSPACE, width, true);
    }

    /**
     * Open the division spaces filter module
     * This opens the drawer and activates the division spaces filter module
     * @param {number} width - Optional width parameter (defaults to WIDTH constant)
     */
    function openDivisionSpacesFilter(width = FULL_OVERLAY_WIDTH) {
        openDrawer(MODULES.DIVISION_SPACES_FILTER, width, true);
    }

    function openHistory(width = FULL_OVERLAY_WIDTH) {
        openDrawer(MODULES.HISTORY, width, true);
    }

    // Expose only the necessary functions and computed properties
    // Internal state remains protected from direct external modification
    return {
        // Constants
        WIDTH,
        FULL_OVERLAY_WIDTH,

        // Functions for controlled state modification
        openQuickAction,
        openSearch,
        openWSCAssociationFilter,
        openNewWorkspace,
        openDivisionSpacesFilter,
        openHistory,
        closeDrawer,
        setWidth,
        setSearchLocating,

        // Computed getters for reactive state checking
        isActiveDrawer,
        isQuickActionActive,
        isSearchActive,
        isSearchLocating,
        isWSCAssociationFilterActive,
        isNewWorkspaceActive,
        isDivisionSpacesFilterActive,
        isHistoryActive,
        currentWidth,
        isFullDrawer,
    };
});
